import { useQuery } from '@apollo/client';
import Box from '@mui/material/Box';
import { ACL_TYPE } from '@openx/types/aclType';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators';
import { useCallback, useMemo } from 'react';
import { type FieldArrayWithId, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useIsAllowed } from '../../../../../../../../configs/permission/utils/useIsAllowed';
import { GetLalSegmentsBuildAudienceDocument } from '../../../../../../../../graphql/query/audiences/GetLalSegmentsBuildAudience.generated';
import { useProviders } from '../../../../../../hooks/useProviders';
import {
  AudienceFields,
  type AudienceForm,
  type AudienceListType,
  type Group,
  type GroupFieldName,
  type LookalikeFormSegment,
  type LookalikeSegmentDefinition,
} from '../../../../../types';
import { ConfirmationModal } from '../../../../ConfirmationModal';
import type { DrawerState } from '../../../drawerTypes';
import { buildLookalikeFilters } from '../../../hooks/utils/lookalikeFilterBuilder';
import { transformLalDefinitions } from '../../../hooks/utils/transformLalDefinitions';
import { LookalikeSelectionModal } from '../../drawer/components/SegmentsList/lookalike/LookalikeSelectionModal';
import { getExistingSegmentsForDefinition } from '../../utils/getExistingSegmentsForDefinition';
import { groupLookalikeDefinitions } from '../../utils/groupLookalikeDefinitions';
import { ExpandableSectionWithAccordion } from '../ExpandableSectionWithAccordion';
import { LookalikeListItem } from './LookalikeListItem';
import { useLookalikeSegmentHandlers } from './useLookalikeSegmentHandlers';

export type LookalikeSegmentsSectionProps = {
  field: FieldArrayWithId<
    AudienceForm,
    'normalized_definition.includes.groups' | 'normalized_definition.excludes.groups',
    'id'
  > &
    Group;
  isEmptyGroup: boolean;
  groupName: string;
  groupFieldName: GroupFieldName;
  listType: AudienceListType;
  isEdit: boolean;
  setDrawerState: (state: DrawerState) => void;
  update: (index: number, value: Group) => void;
  index: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
};

export const LookalikeSegmentsSection = ({
  field,
  groupName,
  groupFieldName,
  listType,
  isEdit,
  setDrawerState,
  update,
  index,
  isExpanded,
  onToggleExpand,
}: LookalikeSegmentsSectionProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext<AudienceForm>();
  const observableAccountId = useWatch({ name: AudienceFields.OrganizationId, control });
  const { allowedProviders } = useProviders(observableAccountId);
  const { isAllowed } = useIsAllowed();

  const isAllowedToSeeLookalikeSection = isAllowed(isAllowedByCompiledAcl(ACL_TYPE.LOOKALIKE_SEGMENTS_ENABLED));
  const lookalikeNotEditable = isEdit && !isAllowedToSeeLookalikeSection;

  const group = field;

  const uniqueDefinitionIds = useMemo(() => {
    const definitionIds = new Set<string>();

    if (group.lookalike?.segments?.length) {
      group.lookalike.segments.forEach(segment => {
        if (segment.lookalike_definition_id) {
          definitionIds.add(segment.lookalike_definition_id);
        }
      });
    }

    return Array.from(definitionIds);
  }, [group]);

  const lookalikeFilters = useMemo(
    () =>
      buildLookalikeFilters({
        filters: {},
        accountId: observableAccountId,
        linkedProviders: allowedProviders || [],
        selectedLookalikeDefinitions: uniqueDefinitionIds,
      }),
    [observableAccountId, allowedProviders, uniqueDefinitionIds]
  );

  const { data: lookalikeDefinitionsData } = useQuery(GetLalSegmentsBuildAudienceDocument, {
    variables: {
      where: lookalikeFilters.where,
      limit: uniqueDefinitionIds.length,
      offset: 0,
    },
    skip: uniqueDefinitionIds.length === 0,
  });

  const lookalikeDefinitionsMap = useMemo(() => {
    const transformedDefinitions = transformLalDefinitions(
      observableAccountId,
      lookalikeDefinitionsData?.lalSegmentFeed
    );

    const definitionsMap = new Map<string, LookalikeSegmentDefinition>();

    transformedDefinitions.forEach(def => {
      definitionsMap.set(def.id, def);
    });

    return definitionsMap;
  }, [lookalikeDefinitionsData, observableAccountId]);

  const groupedDefinitions = useMemo(() => groupLookalikeDefinitions(group?.lookalike?.segments), [group]);

  const orderedDefinitionIds = useMemo(() => {
    const ids: string[] = [];
    const seen = new Set<string>();

    group?.lookalike?.segments?.forEach(segment => {
      const definitionId = segment.lookalike_definition_id;

      if (definitionId && !seen.has(definitionId)) {
        ids.push(definitionId);
        seen.add(definitionId);
      }
    });

    return ids;
  }, [group]);

  const onExpandSection = useCallback(() => {
    if (!isExpanded) onToggleExpand();
  }, [isExpanded, onToggleExpand]);

  const onShrinkSection = useCallback(() => {
    if (isExpanded) onToggleExpand();
  }, [isExpanded, onToggleExpand]);

  const {
    showClearModal,
    showLookalikeModal,
    selectedDefinitionForModal,
    handleSectionToggle,
    handleEditClick,
    handleRemoveDefinition,
    handleEditDefinition,
    handleModalConfirm,
    handleModalClose,
    handleClearButtonClick,
    handleClearConfirm,
    handleClearCancel,
  } = useLookalikeSegmentHandlers({
    fieldAsGroup: group,
    groupName,
    groupFieldName,
    listType,
    index,
    onToggleExpand,
    setDrawerState,
    update,
    lookalikeDefinitionsMap,
    onShrinkSection,
    onExpandSection,
  });

  const allLookalikeSegments = useMemo(() => {
    const segments: LookalikeFormSegment[] = [];

    if (group.lookalike?.segments?.length) {
      segments.push(...group.lookalike.segments);
    }

    return segments;
  }, [group]);

  const segmentCount = group?.lookalike?.segments?.length ?? 0;

  const segmentsTitle =
    segmentCount > 0 ? t('Lookalike Segments [ {count} ]', { count: segmentCount }) : t('Lookalike Segments');

  const editProps = isEdit
    ? {
        showEditButton: true as const,
        showClearButton: segmentCount > 0,
        disableEditButton: lookalikeNotEditable,
        onEditClick: handleEditClick,
        onClear: handleClearButtonClick,
      }
    : {};
  return (
    <>
      <ExpandableSectionWithAccordion
        title={segmentsTitle}
        dataTest="lookalike-segments-section"
        subtitle={segmentCount === 0 ? t('Lookalike segments not applied') : undefined}
        {...editProps}
        expandable={segmentCount > 0}
        isExpanded={isExpanded}
        onToggleExpand={handleSectionToggle}
        expandableSectionStyles={{
          accordionDetails: {
            px: 0,
            maxHeight: '400px',
            overflow: 'auto',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {orderedDefinitionIds.map(definitionId => {
            const segments = groupedDefinitions.get(definitionId);

            if (!segments) return null;

            return (
              <LookalikeListItem
                key={definitionId}
                definition={segments}
                onDelete={() => handleRemoveDefinition(definitionId)}
                onEdit={() => handleEditDefinition(definitionId)}
                isEditDisabled={lookalikeNotEditable || !isEdit}
              />
            );
          })}
        </Box>
      </ExpandableSectionWithAccordion>

      <ConfirmationModal
        isOpen={showClearModal}
        onClose={handleClearCancel}
        onDelete={handleClearConfirm}
        title={t('Clear all lookalike segments')}
        item={t('All lookalike segments')}
      />

      {selectedDefinitionForModal && (
        <LookalikeSelectionModal
          open={showLookalikeModal}
          onClose={handleModalClose}
          onConfirm={handleModalConfirm}
          definition={selectedDefinitionForModal}
          isEditMode
          existingSegments={getExistingSegmentsForDefinition(allLookalikeSegments, selectedDefinitionForModal.id)}
        />
      )}
    </>
  );
};
